import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BookingPolicy,
  ResolvedServicePolicy,
  ServicePolicyOverride,
  SetBookingPolicy,
  SetServicePolicyOverride,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';

/**
 * WS7 phase 2 — booking-policy engine hooks (TanStack Query over the generated client). The salon's
 * money rules (deposit / cancellation window / no-show fee) configured once; the SAME structured
 * rule renders to plain English for every booking surface and enforces itself through the Phase 2
 * payment endpoints. Mirrors data/services.ts / data/scheduling.ts: unwrap()/expectOk() on the
 * typed client, never hand-rolled fetch.
 *
 * RBAC (mirror of the API): reads need `appointments:read` (every booking surface reads policy
 * text); writes (PUT /policy, per-service override upsert/delete) are manager-only + `appointments:
 * write`. The UI gates the editor to managers, so a 403 is never the first feedback a user gets.
 *
 * These query keys are kept LOCAL to this hook (not in the shared lib/query.ts barrel) so the file
 * is self-contained and respects the build's shared-file partition. Policy is salon-scoped, not
 * renter-sealed — the same policy text is read by every booking surface.
 */

const pk = {
  policy: ['policy'] as const,
  versions: ['policy', 'versions'] as const,
  override: (serviceId: string) => ['policy', 'override', serviceId] as const,
  resolved: (serviceId: string, clientId?: string) =>
    ['policy', 'resolved', serviceId, clientId ?? null] as const,
};

// ── Salon policy ────────────────────────────────────────────────────────────────

/** The salon's live booking policy. The API lazily creates the no-charge default on first read. */
export function useBookingPolicy() {
  return useQuery({
    queryKey: pk.policy,
    queryFn: async (): Promise<BookingPolicy> => unwrap(await api.GET('/v1/policy')),
  });
}

/**
 * Replace the salon's policy. A PUT mints a NEW immutable policy_version (an already-booked
 * appointment keeps the terms it was shown — edits are never retroactive). Sub-rules omitted from
 * the body keep their current value, so a lone `armAutoCharge` toggle need not restate the rest.
 */
export function useSetBookingPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetBookingPolicy): Promise<BookingPolicy> =>
      unwrap(await api.PUT('/v1/policy', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pk.policy });
      void qc.invalidateQueries({ queryKey: pk.versions });
      // Resolved text for every service may now differ — drop the whole policy cache subtree.
      void qc.invalidateQueries({ queryKey: ['policy', 'resolved'] });
    },
  });
}

// ── Per-service override ──────────────────────────────────────────────────────────

/**
 * A service's policy override, or null when the service has none (the API 404s in that case — we
 * map it to null rather than an error so the editor can show "uses salon default" cleanly).
 */
export function useServicePolicyOverride(serviceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: pk.override(serviceId ?? ''),
    enabled: Boolean(serviceId) && enabled,
    queryFn: async (): Promise<ServicePolicyOverride | null> => {
      const res = await api.GET('/v1/policy/services/{serviceId}/override', {
        params: { path: { serviceId: serviceId! } },
      });
      if (res.response.status === 404) return null;
      return unwrap(res);
    },
  });
}

export function useSetServicePolicyOverride(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetServicePolicyOverride): Promise<ServicePolicyOverride> =>
      unwrap(
        await api.PUT('/v1/policy/services/{serviceId}/override', {
          params: { path: { serviceId } },
          body: body as never,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pk.override(serviceId) });
      void qc.invalidateQueries({ queryKey: pk.resolved(serviceId) });
    },
  });
}

/** Remove a service's override — it reverts to the salon default. */
export function useDeleteServicePolicyOverride(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> =>
      expectOk(
        await api.DELETE('/v1/policy/services/{serviceId}/override', {
          params: { path: { serviceId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pk.override(serviceId) });
      void qc.invalidateQueries({ queryKey: pk.resolved(serviceId) });
    },
  });
}

// ── Resolved policy text (what a client is shown) ──────────────────────────────────

/**
 * The resolved, plain-English policy for a service: the salon default with any per-service override
 * folded in, plus the policy_version id a booking surface must persist onto the appointment it
 * creates. Read by the editor's live preview to show exactly what a client will see.
 */
export function useResolvedServicePolicy(
  serviceId: string | undefined,
  clientId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: pk.resolved(serviceId ?? '', clientId),
    enabled: Boolean(serviceId) && enabled,
    queryFn: async (): Promise<ResolvedServicePolicy> =>
      unwrap(
        await api.GET('/v1/policy/services/{serviceId}/resolved', {
          params: {
            path: { serviceId: serviceId! },
            query: clientId ? { clientId } : {},
          },
        }),
      ),
  });
}
